import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus, MoreHorizontal } from 'lucide-react';
import type {
  KanbanColumn,
  KanbanCardData,
  KanbanLabel,
  BoardMember,
} from 'types/kanban';
import KanbanCardItem from '../KanbanCardItem';
import styles from './DroppableColumn.module.scss';

export interface DroppableColumnProps {
  column: KanbanColumn;
  cards: Record<string, KanbanCardData>;
  isEditingTitle: boolean;
  editingTitleValue: string;
  onTitleClick: (columnId: string) => void;
  onTitleChange: (value: string) => void;
  onTitleSubmit: () => void;
  onAddCardClick: (columnId: string) => void;
  addingCardColumnId: string | null;
  newCardTitle: string;
  onNewCardTitleChange: (value: string) => void;
  onAddCardConfirm: (columnId: string) => void;
  onAddCardCancel: () => void;
  onOpenListMenu: (columnId: string, e: React.MouseEvent) => void;
  onCardClick: (cardId: string) => void;
  onOpenCardMenu: (
    cardId: string,
    currentColumnId: string,
    e: React.MouseEvent,
  ) => void;
  boardLabels?: KanbanLabel[];
  boardMembers?: BoardMember[];
  sortableListeners?: object;
  sortableAttributes?: object;
  editingCardTitleId?: string | null;
  editingCardTitleValue?: string;
  onCardTitleChange?: (value: string) => void;
  onCardTitleSubmit?: () => void;
  onCardTitleCancel?: () => void;
  isCardDragActive?: boolean;
}

export default function DroppableColumn({
  column,
  cards,
  isEditingTitle,
  editingTitleValue,
  onTitleClick,
  onTitleChange,
  onTitleSubmit,
  onAddCardClick,
  addingCardColumnId,
  newCardTitle,
  onNewCardTitleChange,
  onAddCardConfirm,
  onAddCardCancel,
  onOpenListMenu,
  onCardClick,
  onOpenCardMenu,
  boardLabels = [],
  boardMembers = [],
  sortableListeners,
  sortableAttributes,
  editingCardTitleId = null,
  editingCardTitleValue = '',
  onCardTitleChange,
  onCardTitleSubmit,
  onCardTitleCancel,
  isCardDragActive = false,
}: DroppableColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: { type: 'column', column },
  });

  // Dedupe so the same card id never renders twice (e.g. after REST + WS both apply)
  const uniqueCardIds = useMemo(
    () => Array.from(new Set(column.cardIds)),
    [column.cardIds],
  );
  const columnCards: KanbanCardData[] = uniqueCardIds
    .map((id) => cards[id])
    .filter((c): c is KanbanCardData => Boolean(c));
  const isAddingCard = addingCardColumnId === column.id;
  const isColumnEmpty = uniqueCardIds.length === 0;

  const addCardBtnRef = useRef<HTMLButtonElement | null>(null);
  const [addCardBtnHeightPx, setAddCardBtnHeightPx] = useState(0);

  const addCardFormRef = useRef<HTMLDivElement | null>(null);
  const [addCardFormHeightPx, setAddCardFormHeightPx] = useState(0);

  // Measure the button height so we only reserve the extra part of the
  // absolute-positioned form that overflows beyond the button's slot.
  useLayoutEffect(() => {
    const el = addCardBtnRef.current;
    if (!el) return;

    const measure = () => {
      setAddCardBtnHeightPx(el.getBoundingClientRect().height);
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!isAddingCard) {
      setAddCardFormHeightPx(0);
      return;
    }

    const el = addCardFormRef.current;
    if (!el) return;

    const measure = () => {
      setAddCardFormHeightPx(el.getBoundingClientRect().height);
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isAddingCard, newCardTitle]);

  const addFormOverflowPx = Math.max(
    0,
    addCardFormHeightPx - addCardBtnHeightPx,
  );
  // Cap the reserved padding so the gap doesn't look like a big "extra card".
  const addFormReservePx = Math.min(addFormOverflowPx, 50);

  const cardsListStyle: React.CSSProperties | undefined = isAddingCard
    ? ({
        // Used by .cardsList to reserve only the overflow part in the
        // scroll viewport (so the placeholder doesn't look like a new card).
        '--add-form-height': `${addFormReservePx}px`,
      } as React.CSSProperties)
    : undefined;
  const showCardsViewport =
    uniqueCardIds.length > 0 || (isAddingCard && isColumnEmpty);

  const cardsListRef = useRef<HTMLDivElement | null>(null);

  // When the add-card form opens, jump the scroll container to the bottom
  // so the user sees the newest cards area immediately.
  useLayoutEffect(() => {
    if (!isAddingCard) return;
    requestAnimationFrame(() => {
      const el = cardsListRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    });
  }, [isAddingCard]);

  return (
    <div
      ref={setNodeRef}
      className={`${styles.column} ${
        isCardDragActive ? styles.columnDetectionActive : ''
      }`}
      data-kanban-column="true"
      data-kanban-column-id={column.id}
    >
      <div
        className={`${styles.columnContent} ${
          isOver ? styles.columnDropHighlight : ''
        } ${isAddingCard && isColumnEmpty ? styles.columnEmptyAdding : ''}`}
      >
        <div
          className={styles.columnHeader}
          {...(sortableListeners ?? {})}
          {...(sortableAttributes ?? {})}
        >
          <div className={styles.columnTitleRow}>
            {isEditingTitle ? (
              <input
                type="text"
                className={styles.inputColumnTitle}
                value={editingTitleValue}
                onChange={(e) => onTitleChange(e.target.value)}
                onBlur={onTitleSubmit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onTitleSubmit();
                }}
                autoFocus
                aria-label="List name"
              />
            ) : (
              <button
                type="button"
                className={styles.columnTitleBtn}
                onClick={() => onTitleClick(column.id)}
              >
                {column.title}
              </button>
            )}
            <span className={styles.columnCardCount}>
              {uniqueCardIds.length}
            </span>
          </div>
          <div className={styles.columnMenuWrap}>
            <button
              type="button"
              className={styles.menuTriggerBtn}
              onClick={(e) => {
                e.stopPropagation();
                onOpenListMenu(column.id, e);
              }}
              aria-label={`Menu for list ${column.title}`}
            >
              <MoreHorizontal size={16} />
            </button>
          </div>
        </div>
        <div
          className={
            showCardsViewport
              ? `${styles.cardsList} ${isColumnEmpty ? styles.cardsListEmpty : ''}`
              : ''
          }
          style={cardsListStyle}
          ref={cardsListRef}
        >
          <SortableContext
            items={uniqueCardIds}
            strategy={verticalListSortingStrategy}
          >
            {columnCards.map((card) => (
              <KanbanCardItem
                key={card.id}
                card={card}
                onCardClick={onCardClick}
                onOpenCardMenu={onOpenCardMenu}
                currentColumnId={column.id}
                boardLabels={boardLabels}
                boardMembers={boardMembers}
                isEditingTitle={editingCardTitleId === card.id}
                editingTitleValue={
                  editingCardTitleId === card.id ? editingCardTitleValue : ''
                }
                onTitleChange={onCardTitleChange}
                onTitleSubmit={onCardTitleSubmit}
                onTitleCancel={onCardTitleCancel}
              />
            ))}
          </SortableContext>
        </div>
        <div className={styles.columnFooter}>
          <button
            type="button"
            className={styles.addCardBtn}
            onClick={() => onAddCardClick(column.id)}
            disabled={isAddingCard}
            style={{ visibility: isAddingCard ? 'hidden' : 'visible' }}
            ref={addCardBtnRef}
          >
            <Plus
              size={16}
              style={{ verticalAlign: 'middle', marginRight: 4 }}
            />
            Add card
          </button>
          {isAddingCard && (
            <div ref={addCardFormRef} className={styles.addCardForm}>
              <input
                type="text"
                className={styles.inputCardTitle}
                placeholder="Card title"
                value={newCardTitle}
                onChange={(e) => onNewCardTitleChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onAddCardConfirm(column.id);
                  if (e.key === 'Escape') onAddCardCancel();
                }}
                autoFocus
                aria-label="Card title"
              />
              <div className={styles.formActions}>
                <button
                  type="button"
                  className={`${styles.formBtn} ${styles.primary}`}
                  onClick={() => onAddCardConfirm(column.id)}
                  disabled={!newCardTitle.trim()}
                >
                  Add
                </button>
                <button
                  type="button"
                  className={`${styles.formBtn} ${styles.secondary}`}
                  onClick={onAddCardCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
